// Per-carrier service catalogues for the "Add service" picker on the
// account panel. Keyed by carriers.carrier_code; a carrier with no
// catalogue only offers "Custom service…".
//
// APC lives in apcServiceCatalogue.ts (unchanged). InPost's entries
// mirror what migration 185 seeds, so an admin who deletes a service by
// mistake can put it back with the contract rates pre-filled.
//
// InPost rates: JLI × InPost UK Services Agreement (30/07/2026), ex
// VAT, per PARCEL. 2Door bands (S/M/L) are derived per box from dims +
// weight at pricing time; the service limits are the Large band
// ceiling (120 cm longest side, 30 kg). Fuel 5%, decarbonisation 5p,
// NI £1 and remote-area £4.50 are added by destination (carrier
// config), so the picker says so.

import { APC_SERVICE_CATALOGUE, type ApcCatalogueService } from "./apcServiceCatalogue";
import type { CarrierServicePricing } from "./types";

export interface CatalogueService extends ApcCatalogueService {
    pricing?: CarrierServicePricing | null;
    // Which account label this entry belongs on (InPost: "2door" / "ooh").
    accountLabel?: string;
}

export const INPOST_SERVICE_CATALOGUE: CatalogueService[] = [
    {
        code: "IP2DSTD",
        name: "InPost 2Door Standard (2-4 day)",
        itemType: "PARCEL",
        maxPieces: null,
        maxWeightGrams: 30000,
        maxLengthCm: 120, maxWidthCm: null, maxHeightCm: null,
        baseCost: 2.00, additionalParcelCost: null, includedKg: null, perKgCost: null,
        pricing: { priority: "STANDARD", bands: { S: 2.00, M: 2.35, L: 3.50 } },
        accountLabel: "2door",
        surchargeNote: "per parcel by size band + fuel 5% + 5p decarbonisation (+ NI £1 / remote £4.50)",
    },
    {
        code: "IP2DEXP",
        name: "InPost 2Door Express (next day)",
        itemType: "PARCEL",
        maxPieces: null,
        maxWeightGrams: 30000,
        maxLengthCm: 120, maxWidthCm: null, maxHeightCm: null,
        baseCost: 2.30, additionalParcelCost: null, includedKg: null, perKgCost: null,
        pricing: { priority: "EXPRESS", bands: { S: 2.30, M: 2.65, L: 3.50 } },
        accountLabel: "2door",
        surchargeNote: "per parcel by size band + fuel 5% + 5p decarbonisation (+ NI £1 / remote £4.50)",
    },
    {
        code: "IPOOHSTD",
        name: "InPost Locker / Shop (2-4 day)",
        itemType: "PARCEL",
        maxPieces: 1,
        maxWeightGrams: 15000,
        maxLengthCm: 64, maxWidthCm: 41, maxHeightCm: 38,
        baseCost: 1.90, additionalParcelCost: null, includedKg: null, perKgCost: null,
        pricing: { priority: "STANDARD", flat: 1.90 },
        accountLabel: "ooh",
        surchargeNote: "flat per parcel; needs the buyer's InPost point on the order",
    },
];

// DPD Poland (DPDServices). No tariff is pre-filled — the PLN contract
// rates are entered per service (base per shipment + per parcel + per
// kg over the allowance; the cost model prices DPD/Cargus that way).
// The code is ours; what goes on the wire is the services[] list on
// `pricing.dpd` (see BE dpdPayload.dpdServicesFor): CLASSIC sends no
// service code at all (DPD's default domestic product). Limits: 31.5 kg
// is DPD's standard parcel ceiling; the API accepts up to 700 kg.
// Two CONTRACTS run from the one Poland account — "EU-DPD Parcel Classic
// (Local)" for PL and "EU-DPD Parcel Classic (INTER)" for the rest of the
// EU — so every DPD service carries a scope (pricing.dpd.scope). The BE
// treats a service on the wrong side of the border as unfit: a rule
// naming the Local service falls through for a German order, and the
// bench refuses outright if a rule is edited under it. The INTER row can
// carry its own payer FID (pricing.dpd.payer_fid) when DPD bill that
// contract under a different customer number.
export const DPD_SERVICE_CATALOGUE: CatalogueService[] = [
    {
        code: "CLASSICLOCAL",
        name: "EU-DPD Parcel Classic (Local) — Poland, next business day",
        itemType: "PARCEL",
        maxPieces: null, maxWeightGrams: 31500, maxLengthCm: 175, maxWidthCm: null, maxHeightCm: null,
        baseCost: null, additionalParcelCost: null, includedKg: null, perKgCost: null,
        // PRIVATE_PERSON: Jobalots buyers are consumers — DPD prints PRIV
        // (not BIZ) and handles the parcel as a home delivery, as the
        // previous WMS did.
        pricing: { dpd: { scope: "domestic", services: [{ code: "PRIVATE_PERSON" }] } },
        surchargeNote: "domestic contract (PLN); PL destinations only; private recipient (PRIV)",
    },
    {
        code: "CLASSICINTER",
        name: "EU-DPD Parcel Classic (INTER) — EU by road",
        itemType: "PARCEL",
        maxPieces: null, maxWeightGrams: 31500, maxLengthCm: 175, maxWidthCm: null, maxHeightCm: null,
        baseCost: null, additionalParcelCost: null, includedKg: null, perKgCost: null,
        pricing: { dpd: { scope: "international", services: [], content: "Consumer goods" } },
        surchargeNote: "international contract; EU destinations only; parcel content is sent (customs-free)",
    },
    {
        code: "SATURDAY",
        name: "DPD Classic (Local) + Saturday delivery",
        itemType: "PARCEL",
        maxPieces: null, maxWeightGrams: 31500, maxLengthCm: 175, maxWidthCm: null, maxHeightCm: null,
        baseCost: null, additionalParcelCost: null, includedKg: null, perKgCost: null,
        pricing: { dpd: { scope: "domestic", services: [{ code: "SATURDAY" }] } },
        surchargeNote: "Classic + the SATURDAY service",
    },
    {
        code: "NEXTDAY",
        name: "DPD Next Day (Local, guaranteed next day)",
        itemType: "PARCEL",
        maxPieces: null, maxWeightGrams: 31500, maxLengthCm: 175, maxWidthCm: null, maxHeightCm: null,
        baseCost: null, additionalParcelCost: null, includedKg: null, perKgCost: null,
        pricing: { dpd: { scope: "domestic", services: [{ code: "DPD_NEXT_DAY" }] } },
        surchargeNote: "DPD_NEXT_DAY service",
    },
    {
        code: "PICKUP",
        name: "DPD Pickup (Local, parcel shop / locker)",
        itemType: "PARCEL",
        maxPieces: 1, maxWeightGrams: 20000, maxLengthCm: 100, maxWidthCm: null, maxHeightCm: null,
        baseCost: null, additionalParcelCost: null, includedKg: null, perKgCost: null,
        pricing: { dpd: { scope: "domestic", services: [], pickup: true } },
        surchargeNote: "single parcel; needs the buyer's DPD Pickup point id on the order",
    },
    {
        code: "B2C",
        name: "DPD B2C (Local, predict delivery window)",
        itemType: "PARCEL",
        maxPieces: null, maxWeightGrams: 31500, maxLengthCm: 175, maxWidthCm: null, maxHeightCm: null,
        baseCost: null, additionalParcelCost: null, includedKg: null, perKgCost: null,
        pricing: { dpd: { scope: "domestic", services: [{ code: "B2C" }] } },
        surchargeNote: "needs DPD account-manager enablement",
    },
];

// Cargus (UrgentCargus API v3). The service code IS Cargus's ServiceId
// (annex of the API doc): 34/35/36 single piece by weight band, 39/40
// multipiece ("Economic Standard M", ≤15 pieces ≤31 kg each). Rates in
// RON per shipment; the multipiece limits are enforced BE-side too.
export const CARGUS_SERVICE_CATALOGUE: CatalogueService[] = [
    {
        code: "39",
        name: "Cargus Standard (multipiece, ≤31 kg/parcel)",
        itemType: "PARCEL",
        maxPieces: 15, maxWeightGrams: 31000, maxLengthCm: null, maxWidthCm: null, maxHeightCm: null,
        baseCost: null, additionalParcelCost: null, includedKg: null, perKgCost: null,
        pricing: { cargus: { service_id: 39 } },
        surchargeNote: "ServiceId 39 — Economic Standard M; contract rate (RON) per shipment",
    },
    {
        code: "40",
        name: "Cargus Standard 31+ (multipiece, 31–50 kg/parcel)",
        itemType: "PARCEL",
        maxPieces: 15, maxWeightGrams: 50000, maxLengthCm: null, maxWidthCm: null, maxHeightCm: null,
        baseCost: null, additionalParcelCost: null, includedKg: null, perKgCost: null,
        pricing: { cargus: { service_id: 40 } },
        surchargeNote: "ServiceId 40 — Economic Std. M Plus",
    },
    {
        code: "34",
        name: "Cargus Standard (single parcel ≤31 kg)",
        itemType: "PARCEL",
        maxPieces: 1, maxWeightGrams: 31000, maxLengthCm: null, maxWidthCm: null, maxHeightCm: null,
        baseCost: null, additionalParcelCost: null, includedKg: null, perKgCost: null,
        pricing: { cargus: { service_id: 34 } },
        surchargeNote: "ServiceId 34 — door to door, one piece",
    },
    {
        code: "35",
        name: "Cargus Standard 31+ (single parcel 31–50 kg)",
        itemType: "PARCEL",
        maxPieces: 1, maxWeightGrams: 50000, maxLengthCm: null, maxWidthCm: null, maxHeightCm: null,
        baseCost: null, additionalParcelCost: null, includedKg: null, perKgCost: null,
        pricing: { cargus: { service_id: 35 } },
        surchargeNote: "ServiceId 35",
    },
    {
        code: "36",
        name: "Cargus Standard 50+ (single parcel, pallet)",
        itemType: "PARCEL",
        maxPieces: 1, maxWeightGrams: 800000, maxLengthCm: null, maxWidthCm: null, maxHeightCm: null,
        baseCost: null, additionalParcelCost: null, includedKg: null, perKgCost: null,
        pricing: { cargus: { service_id: 36 } },
        surchargeNote: "ServiceId 36 — pallet; single piece only",
    },
];

// InPost's wire has no item type; the service row still stores one for
// the shared schema, so the picker offers just PARCEL. DPD/Cargus the same.
export const ITEM_TYPES_BY_CARRIER: Record<string, string[]> = {
    apc: ["PARCEL", "PACK", "LIQUIDS", "LIMITED QUANTITIES"],
    inpost: ["PARCEL"],
    dpd: ["PARCEL"],
    cargus: ["PARCEL"],
};

export const itemTypesFor = (carrierCode: string): string[] =>
    ITEM_TYPES_BY_CARRIER[carrierCode] ?? ["PARCEL"];

export const catalogueFor = (carrierCode: string, accountLabel?: string): CatalogueService[] => {
    if (carrierCode === "apc") return APC_SERVICE_CATALOGUE;
    if (carrierCode === "inpost") {
        return INPOST_SERVICE_CATALOGUE.filter((s) => !accountLabel || !s.accountLabel || s.accountLabel === accountLabel);
    }
    if (carrierCode === "dpd") return DPD_SERVICE_CATALOGUE;
    if (carrierCode === "cargus") return CARGUS_SERVICE_CATALOGUE;
    return [];
};

// How the account authenticates — drives which credential fields the
// account panel shows:
//   email  — login email + password (APC)
//   oauth  — client id + organisation id + client secret (InPost)
//   dpd    — login + password + master FID + payer FID (DPD Poland)
//   cargus — username + password + APIM subscription key + pickup point (Cargus)
//   jas    — client id + client secret + username + password (the Extranet 2
//            login) + the shipper's NIP (JAS ShipmentGateway, OAuth password grant)
//   raben  — SOAP username + password (WSS UsernameToken) + NIP + the EDI /
//            payer / product / eWZ settings in account.config.raben (GS1 XML)
export type CredentialKind = "email" | "oauth" | "dpd" | "cargus" | "jas" | "raben";

export const credentialKindFor = (carrierCode: string): CredentialKind => {
    if (carrierCode === "inpost") return "oauth";
    if (carrierCode === "dpd") return "dpd";
    if (carrierCode === "cargus") return "cargus";
    if (carrierCode === "jas") return "jas";
    if (carrierCode === "raben") return "raben";
    return "email";
};

// Carriers that print/route on the account's sender + origin address
// (everything but APC, whose depot holds the address).
export const usesSenderAddress = (carrierCode: string): boolean => credentialKindFor(carrierCode) !== "email";

// Does this carrier price per parcel by size band (InPost) rather than
// per consignment with a per-kg escalator (APC)? Drives which tariff
// columns the services table shows.
export const usesBandPricing = (carrierCode: string): boolean => carrierCode === "inpost";

// Does this carrier authenticate with OAuth client credentials (client
// id + secret + organisation id) rather than an email + password login?
export const usesOAuth = (carrierCode: string): boolean => carrierCode === "inpost";

// Does this carrier authenticate with one API key per account and nothing
// else (Palletways)? The key rides the same masked secret field as a
// password.
export const usesApiKey = (carrierCode: string): boolean => carrierCode === "palletways";

// Pallet carriers are booked from the pallet desk, never through a shipping
// rule: the BE refuses to create a service for them, so the account panel
// shows credentials (and, for JAS, the sender address its loading place
// needs) plus Test connection, but no collection, customs or services
// sections. Palletways (BE migration 201), JAS (ShipmentGateway, 246), Raben
// (SOAP GS1 XML) and the manual DB Schenker all qualify.
export const isConnectionOnly = (carrierCode: string): boolean =>
    carrierCode === "palletways" || carrierCode === "jas" || carrierCode === "raben" || isManualPalletCarrier(carrierCode);

// DB Schenker (Poland → rest of the EU pallets, BE registry bookingMode
// 'manual'): no API at all — the operator books on the carrier's site and
// ships from the pallet desk with the label attached to the order. The card
// has no credentials, no services and no Training/Live switch; Test
// connection is answered by the BE without calling anything. (JAS left this
// set on 2026-10-23 when its ShipmentGateway API went live in Portal.)
export const MANUAL_PALLET_CARRIERS = new Set(["dbschenker"]);
export const isManualPalletCarrier = (carrierCode: string): boolean => MANUAL_PALLET_CARRIERS.has(carrierCode);
// Pallet carriers whose loading place is the account's sender address (JAS, Raben).
export const usesLoadingPlace = (carrierCode: string): boolean => carrierCode === "jas" || carrierCode === "raben";
